import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { apiResponse } from '../schemas/common.schema';

const c = initContract();
const dateish = z.union([z.string(), z.date()]).optional();

const StudyProgressCourseInfoSchema = z.object({
  _id: z.string(),
  course_title: z.string().optional(),
  course_image: z.string().optional(),
  course_url_token: z.string().optional(),
  course_description: z.string().optional(),
});

export const StudyProgressResponseSchema = z.object({
  _id: z.string(),
  contact_id: z.string().optional(),
  contact_token: z.string().optional(),
  course_id: z.string().optional(),
  course_started_at: dateish,
  course_finished_at: z.union([z.string(), z.date()]).nullable().optional(),
  course_last_activity: dateish,
  course_lessons: z.array(z.number()).optional(),
  course_lessons_amount: z.number().optional(),
  course_level: z.number().optional(),
  course: StudyProgressCourseInfoSchema.optional(),
});

const StudentLessonSchema = z.object({
  order: z.number(),
  title: z.string(),
  token: z.string().optional(),
  studyingToken: z.string().optional(),
  status: z.enum(['done', 'reviewing', 'active', 'locked']),
});

/**
 * One course in the student's progress. `status` is per-course — the top-level page state
 * (no_course / pending / active / completed) is DERIVED from this list on the client, not sent.
 *   active    — in progress; continue via the active lesson
 *   pending   — selected, agent still preparing the first lesson (no lessons yet)
 *   completed — every lesson reviewed
 */
const CourseProgressSchema = z.object({
  courseId: z.string(),
  title: z.string(),
  image: z.string().optional(),
  locale: z.string().optional(),
  status: z.enum(['active', 'pending', 'completed']),
  totalLessons: z.number(),
  completedOrders: z.array(z.number()),
  currentOrder: z.number(),
  lessons: z.array(StudentLessonSchema),
  activeStudyingToken: z.string().optional(),
  startedAt: z.string().optional(),
  finishedAt: z.string().optional(),
  // pending-only — the study request being processed
  requestId: z.string().optional(),
});

const StudentProgressSchema = z.object({
  contactToken: z.string(),
  studyBaseUrl: z.string(),
  // UI language — from the active/most-recent course.
  locale: z.string().optional(),
  // The full progress = the list of courses. Active/pending first, then finished (newest first).
  // Empty list ⇒ "no course yet".
  courses: z.array(CourseProgressSchema).default([]),
});

export const studyProgressContract = c.router({
  // OTP login: send 6-digit code to email
  sendOtp: {
    method: "POST",
    path: "/my/auth/otp/send",
    body: z.object({ email: z.string().email(), locale: z.string().optional() }),
    responses: {
      200: z.object({ success: z.boolean() }),
      404: z.object({ message: z.string() }),
    },
    summary: "Send OTP code to email",
  },
  // OTP verify: exchange code for session cookie
  verifyOtp: {
    method: "POST",
    path: "/my/auth/otp/verify",
    body: z.object({ email: z.string().email(), otp: z.string().length(6) }),
    responses: {
      200: z.object({ success: z.boolean() }),
      401: z.object({ message: z.string() }),
    },
    summary: "Verify OTP and issue session cookie",
  },
  // OTP register: create contact + send OTP
  registerOtp: {
    method: "POST",
    path: "/my/auth/otp/register",
    body: z.object({ email: z.string().email(), fullname: z.string().min(1), locale: z.string().optional() }),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: "Register new contact and send OTP",
  },
  // Magic-link login: exchanges student_access_token for httpOnly session cookie
  studentLogin: {
    method: 'POST',
    path: '/my/auth/token',
    body: z.object({ accessToken: z.string().min(1) }),
    responses: {
      200: z.object({ success: z.boolean() }),
      401: z.object({ message: z.string() }),
    },
    summary: 'Exchange student_access_token for httpOnly session cookie (magic-link login)',
  },
  // Profile: returns student contact info (name, email)
  getMyProfile: {
    method: "GET",
    path: "/my/profile",
    responses: {
      200: z.object({
        contact_fullname: z.string().optional(),
        contact_email: z.string().optional(),
        contact_token: z.string(),
      }),
      401: z.object({ message: z.string() }),
    },
    summary: "Get student profile info (cookie-authenticated)",
  },
  // Profile update: change display name
  updateMyProfile: {
    method: "PATCH",
    path: "/my/profile",
    body: z.object({ fullname: z.string().min(1).max(100) }),
    responses: {
      200: z.object({ success: z.boolean() }),
      401: z.object({ message: z.string() }),
    },
    summary: "Update student display name (cookie-authenticated)",
  },
  // Google OAuth: return Google auth URL as JSON (frontend builds the link)
  googleAuthUrl: {
    method: 'GET',
    path: '/my/auth/google/url',
    query: z.object({ redirect: z.string().optional(), accountId: z.string().optional() }),
    responses: {
      200: z.object({ url: z.string() }),
      503: z.object({ message: z.string() }),
    },
    summary: 'Return Google OAuth consent URL as JSON',
  },
  // Google OAuth: redirect to Google consent screen
  googleAuthRedirect: {
    method: 'GET',
    path: '/my/auth/google',
    query: z.object({ redirect: z.string().optional() }),
    responses: { 302: z.unknown() },
    summary: 'Redirect to Google OAuth consent screen',
  },
  // Google OAuth: handle callback from Google
  googleAuthCallback: {
    method: 'GET',
    path: '/my/auth/google/callback',
    query: z.object({ code: z.string().optional(), state: z.string().optional(), error: z.string().optional() }),
    responses: { 302: z.unknown() },
    summary: 'Handle Google OAuth callback, issue session cookie, redirect to UI',
  },
  // Account deletion: remove contact + progress records
  deleteMyAccount: {
    method: "DELETE",
    path: "/my/account",
    body: z.object({}).optional(),
    responses: {
      200: z.object({ success: z.boolean() }),
      401: z.object({ message: z.string() }),
    },
    summary: "Delete student account and all progress (cookie-authenticated)",
  },
  // Logout: clears httpOnly session cookie
  studentLogout: {
    method: 'POST',
    path: '/my/auth/logout',
    body: z.object({}).optional(),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: 'Clear student session cookie (logout)',
  },
  selectMyCourse: {
    method: 'POST' as const,
    path: '/my/select-course',
    body: z.object({ courseId: z.string(), source: z.string().optional() }),
    responses: {
      200: z.object({ ok: z.literal(true), aiEnabled: z.boolean() }),
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Authenticated student selects a course: creates request + links course + triggers agent in one call',
  },
  // Authenticated progress — reads session from httpOnly cookie (credentials: include)
  getMyProgress: {
    method: 'GET',
    path: '/my/progress',
    responses: {
      200: StudentProgressSchema,
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
    },
    summary: 'Get student course progress (cookie-authenticated)',
  },
  // Legacy: token-in-URL (kept for backward compat)
  getStudentProgress: {
    method: 'GET',
    path: '/my/progress/:contactToken',
    pathParams: z.object({ contactToken: z.string() }),
    responses: {
      200: StudentProgressSchema,
      404: z.object({ message: z.string() }),
    },
    summary: 'Get student course progress by contact token (permanent student identifier)',
  },
  getStudyProgress: {
    method: 'GET',
    path: '/study-progress',
    query: z.object({
      cursor: z.string().optional(),
      limit: z.coerce.number().optional(),
      contact_id: z.string().optional(),
      contact_token: z.string().optional(),
      course_id: z.string().optional(),
    }),
    responses: { 200: apiResponse(z.array(StudyProgressResponseSchema)) },
  },
  createOrUpdateStudyProgress: {
    method: 'POST',
    path: '/study-progress',
    body: z.object({
      contact_id: z.string(),
      contact_token: z.string(),
      course_id: z.string(),
      course_lessons_amount: z.coerce.number(),
      lesson_order: z.coerce.number(),
    }),
    responses: { 200: apiResponse(StudyProgressResponseSchema) },
  },
});

export type StudyProgressResponseDto = z.infer<typeof StudyProgressResponseSchema>;
export type CourseProgressDto = z.infer<typeof CourseProgressSchema>;
export type StudentProgressDto = z.infer<typeof StudentProgressSchema>;
